/**
 * Korean (`ko`) string catalogue — mirrors the key shape of `./en.ts`.
 * Brand names (`OVERVOLT`, `Sniper Rail`, `Arc Coil`, `Cryo Emitter`,
 * `Splash Mortar`, `Pulse Turret`, `Pylon`, `Flux`) stay in English; the
 * rest translates idiomatically into terse, gamey Korean using standard
 * tower-defense conventions (웨이브 / 업그레이드 / 해금 / 난이도 etc.).
 */

import type { Catalogue } from './en';

export const ko: Catalogue = {
  meta: {
    name: '한국어',
    code: 'KO',
  },

  towers: {
    pulse: {
      label: 'Pulse Turret',
      short: '펄스',
      role: '중립 만능형. 경로 어디서나 안정적인 피해.',
    },
    arc: {
      label: 'Arc Coil',
      short: '아크',
      role: '전력망 연동. Pylon 잉여시 강해지고, 정전시 약해진다.',
    },
    cryo: {
      label: 'Cryo Emitter',
      short: '크라이오',
      role: '병목 페인터. 사거리 짧음 — 한 지점의 적들을 둔화·취약 표시.',
    },
    mortar: {
      label: 'Splash Mortar',
      short: '박격포',
      role: '곡사 화력. 경로에서 멀리 설치 — 근거리는 사각지대.',
    },
    sniper: {
      label: 'Sniper Rail',
      short: '스나이퍼',
      role: '직선 관통. 긴 직선에서 강력, 코너에서 약함.',
    },
    pylon: {
      label: 'Pylon',
      short: 'PYLON',
      role: '전력 공급. 타워 수요를 감당하도록 충분히 (Arc용 잉여 포함).',
    },
    wall: {
      label: 'Wall Node',
      short: '벽',
      role: '저렴한 방벽 — Maze 모드 전용. 적의 경로를 만든다.',
    },
  },

  roleHints: {
    pulse: 'Pulse Turret: 믿음직한 기본 — 경로 어디서나 견고한 피해.',
    arc: 'Arc Coil: 전력 잉여로 성장. Pylon을 더 지어 사슬을 강화하라.',
    cryo: 'Cryo Emitter: 정밀한 병목 페인트. 압축점의 몇 칸을 둔화·표시.',
    mortar: 'Splash Mortar: 곡사. 경로에서 멀리 설치 — 근거리는 안 맞는다.',
    sniper: 'Sniper Rail: 직선 관통. 긴 직선에 맞추면 효과 최대.',
    pylon: 'Power Pylon: 전력망에 공급. 타워 수요를 감당하도록 충분히.',
    wall: '벽: 저렴한 방벽 — 적이 지나야 할 경로를 만든다.',
  },

  hints: {
    build:
      '타워 건설: 아래에서 하나 선택(또는 1), 경로 옆 빛나는 타일을 클릭.',
    placed: '좋아. 하나 더 짓고 스페이스로 웨이브를 시작하라.',
    wave1: '타워는 자동 발사. 전력 바가 빨간 영역에 가지 않게 주의.',
    select: '설치된 타워를 클릭해 선택 — U 업그레이드, S 판매.',
    brownout: '정전! 타워 둔화. Pylon(3)을 지어 전력을 보충하라.',
  },

  contextHints: {
    overload:
      '오버로드: 다음 웨이브를 올려 보상↑ — 위험도 올라간다. 시작이 느린가? 끌어올려 Flux를 빨리 모아라.',
    profileKey: 'P를 눌러 프로필 — Flux로 새 타워·스킬·모드를 해금.',
    endlessMod: 'Endless는 웨이브 MOD를 순환 — 구성에 맞춰 조정하라.',
    puzzleHover:
      '퍼즐 모드: 시야 차단 벽을 가진 수제 논리 레벨. 완전히 다른 게임.',
  },

  hud: {
    buildZone: '건설',
    waveZone: '웨이브',
    abilitiesZone: '스킬',
    overloadZone: '오버로드',

    startWave: '웨이브 시작',
    nextWave: '다음 웨이브',
    pause: '일시정지',
    resume: '재개',
    restart: '재시작',
    deployHint: '시작 [SPACE]',
    holdHint: '정지 [SPACE]',
    rebootHint: '재시작 [R]',
    nextWaveHint: '다음 웨이브',

    audioOn: '오디오 켜짐',
    audioOff: '오디오 꺼짐',

    credits: '크레딧',
    core: '코어',
    wave: '웨이브',
    endless: 'ENDLESS',
    score: '점수',
    powerGrid: '전력망',

    runMode: '모드',
    difficulty: '난이도',
    profile: '프로필',
    map: '맵',
    clickHint: '▷ 클릭',

    locked: '잠김',
    lockedShort: '잠김',
    lockedTooltip:
      '잠김 — 프로필 화면에서 ✦{cost} Flux로 해금(P).',
    lockedTowerNotice: '{tower} 잠김',
    lockedAbility: '스킬 잠김',
    lockedOverload: '오버로드 단계 잠김',
    lockedMode: '{mode} 모드 잠김',
    lockedDifficulty: 'Hard 잠김',

    cheatPip: '🔓 CHEAT',
    profilePip: '👤 {name}',

    overloadIdle: '— 오버로드는 1웨이브부터 적용 —',
    overloadTier: 'TIER {tier} · {name}',
    overloadActive: '◉ 발동 {sec}s',

    maxLevel: '최대 레벨',
    upgrade: '업그레이드 L{level}',
    sell: '판매',
    target: '대상: {mode}',
    cycleTarget: '전환 · [T]',
    sellHint: '+{refund}c · [S]',
    upgradeHint: '{cost}c · [U]',
    overclock: '오버클럭',
    overclockOn: '오버클럭 ✦ ON',

    // Sprint 69 (i18n-gaps-extract-s69) — extracted from render.ts / hud.ts.
    buildHintIntro: "건설 단계  //  타워 선택 [1·2·3·4·5] 배치 후 START WAVE [SPACE]",
    buildHintReinforce: "건설 단계  //  그리드 강화 후 START WAVE {wave} [SPACE]",
    mazeRoute: "미 로   경 로",
    tiles: "타일",
    abilityActive: "◉ 활성 {sec}s",
    costSuffix: '{cost}c',
    overclockPip: 'OC ×{n}',
    powerReadout: '{demand} / {supply}W  ·  {pct}%',
    towerCostDraw: '{cost}c {power}w',
    pylonCostSupply: '{cost}c +{supply}w',
    wallCost: '{cost}c 0w',
    endlessModBadge: 'MOD · {label}',
    startWaveOverload: '⚡{n} {tierName}',
    waveOverloadPip: '⚡{n} {tierName}',
    overclockNoticeOn: "{tower} 오버클럭 ON",
    overclockNoticeOff: "{tower} 오버클럭 OFF",

    tierMult: {
      hp: "HP",
      sp: "속",
      cn: "수",
      gd: "금",
      sc: "점",
    },
    spawnShort: {
      bit: "Bit",
      packet: "Pkt",
      daemon: "Dmn",
      fork: "Fork",
      cipher: "Cph",
      patch: "Pch",
      buffer: "Buf",
      streak: "Stk",
      kernel: "커널",
    },
  },

  modes: {
    campaign: '캠페인',
    endless: 'ENDLESS',
    maze: 'MAZE',
    puzzle: '퍼즐',
  },
  modeSub: {
    campaign: '10웨이브 · 승리',
    endless: '✦ {cost}',
    maze: '✦ {cost}',
    puzzle: '레벨 선택',
  },
  modeBlurb: {
    campaign: '10웨이브. 10웨이브에 보스. 이기면 졸업이다.',
    endless: '같은 지도. 매 웨이브 모디파이어. 적응하거나 뚫린다.',
    maze: '개방형 필드. 벽으로 경로를 만든다. 늦추거나 보상을 깎아라.',
    puzzle: '수제 레벨 10. 유일한 승리 배치를 찾아라.',
  },
  modeCard: {
    campaign: {
      tagline: '그리드를 익히고 보스를 잡아라.',
      bullet1: '수동 조율된 10웨이브 + 보스',
      bullet2: '고정 S자 경로 — 짓고, 웨이브, 업그레이드',
      bullet3: '승리 시 이 맵의 Endless 해금',
      length: '약 8분',
      skill: '건설 순서를 단련',
    },
    endless: {
      tagline: '적응하거나 뚫린다. 끝은 없다.',
      bullet1: 'Campaign 1–10 후 영원히 스케일',
      bullet2: '매 웨이브 새 모디파이어',
      bullet3: '코어가 뚫리면 종료',
      length: '약 30분',
      skill: '장기 전환을 단련',
    },
    maze: {
      tagline: '적이 걸을 미로를 네가 짓는다.',
      bullet1: '개방형 — 미리 그려진 경로 없음',
      bullet2: '타워와 저렴한 벽이 경로를 만든다',
      bullet3: '경로가 길수록 사격 시간이 늘어난다',
      length: '약 12분',
      skill: '공간 라우팅을 단련',
    },
    puzzle: {
      tagline: '이기는 배치는 하나. 그것을 찾아라.',
      bullet1: '10 레벨, 유일한 해',
      bullet2: '고정 예산과 고정 웨이브',
      bullet3: '시야 차단 벽 + 타워 한도',
      length: '레벨당 약 2분',
      skill: '추론을 단련',
    },
  },
  difficulty: {
    easy: '쉬움',
    normal: '보통',
    hard: '어려움',
  },
  difficultySub: {
    easy: '완만',
    normal: '균형',
    hard: '✦ {cost}',
  },

  overlay: {
    paused: '일시정지',
    pauseHint: '스페이스 로 재개',

    missionComplete: '// 임무 완수',
    gridSecured: '전력망 확보',
    wavesRepelled: '{total}번의 돌격 웨이브를 모두 격퇴.',
    continueEndless: '계속  →  ENDLESS',
    unlockEndless: '프로필에서 Endless를 해금해 계속하라',
    restartCampaign: '또는 R로 캠페인 재시작',

    systemFailure: '// 시스템 실패',
    coreBreached: '코어 돌파',
    endlessEnded: 'Endless 진행이 {wave}웨이브에서 종료.',
    reactorOverrun: '리액터가 {total}중 {wave}웨이브에서 함락.',
    runItBack: 'R 로 다시 도전',

    finalScore: '최종 점수',
    newHighScore: '★  최고 점수 갱신  ★',
    newEndlessBest: '★  ENDLESS 최고 기록 갱신  ★',
    highScore: '최고 점수',
    endlessBest: 'ENDLESS 최고',

    // Sprint 69 — top-banner countdown overlays drawn by render.ts.
    aftershock: "// 여진 — 그리드 상환  {sec}s //",
    overcharge: "// OVERCHARGE — 그리드 강화  {sec}s //",
    brownout: "// 그리드 강하 — 타워 제한 //",
  },

  profile: {
    title: '프로필',
    subtitle: '// 메타-진행',
    closeHint: 'P 또는 ESC 로 닫기',
    tabUnlocks: '해금',
    tabScoreboard: '순위',
    flux: 'FLUX',
    unlockTree: '해금 트리',
    unlockedCount: '{owned} / {total} 해금',
    lifetime: '누적',

    branchArsenal: '병기고',
    branchModes: '모드',
    branchChallenge: '도전',
    branchCosmetic: '치장',

    statRunsPlayed: '플레이 수',
    statCampaignWins: '캠페인 승리',
    statBestScore: '최고 점수',
    statDeepestEndless: '최심 ENDLESS',
    statTotalFlux: '누적 FLUX',

    personalBests: '개인 기록',
    waveLabel: '{wave}웨이브',

    runHistory: '플레이 기록',
    noRuns: '아직 기록 없음 — 한 판 마치고 등반을 시작하라.',
    earlierRuns: '+ {n}건의 이전 기록',
    earlierRunsPlural: '+ {n}건의 이전 기록',
    colRun: '런',
    colMode: '모드 · 난이도',
    colResult: '결과',
    colOverload: 'OVL',
    colScore: '점수',
    colDeltaBest: 'Δ 최고',
    colFlux: 'FLUX',
    starBest: '★ 최고',

    statusUnlocked: '해금됨',
    statusOwned: '보유',
    statusRequiresPrereq: '먼저 {prereq}',
    statusFluxShort: '✦{short} Flux 부족',
    statusAffordable: '가능 — 클릭해 해금',
    statusCost: '해금 ✦{cost}',
    buyAction: '구매  ▸',
    nodeReq: '요구: {prereq}',

    profileLauncher: '프로필',
    profileKeyHint: '[ P ]',
    viewScoreboard: '순위 보기',
    newPersonalBest: '★  개인 기록 갱신  ★',
    fromYourBest: '최고 대비',

    grantTower: '무기 타워',
    grantAbility: '액티브 스킬',
    grantMode: '게임 모드',
    grantDifficulty: '난이도',
    grantOverload: '오버로드 TIER {tier}',
    grantTheme: '보드 테마',

    // Sprint 69 — unlock-tree hover preview.
    nodePreview: {
      towerMortar: "간접 사격. 경로 뒤에 배치 — 근접은 데드존; 군집에 포탄, 장갑 깎기.",
      towerSniper: "시야 관통 랜스. 긴 직선 조준 — 라인 위 모든 적이 피격; 코너에서 약함.",
      abilityAirstrike: "대상 타일에 일격 폭발 (45c). 군집 정리 또는 큰 적 깎기.",
      abilityEmp: "필드 스턴 (80c). 레인이 무너질 때 모든 적을 잠시 동결.",
      modeEndless: "웨이브 10 너머 — 코어가 떨어질 때까지 절차적 웨이브.",
      modeMaze: "개방 필드 건설 모드. 타워는 타일을 막고 적은 A*-회피.",
      difficultyHard: "더 어려운 웨이브; 더 큰 골드/점수 배율.",
      overloadCritical: "Overload 3단 — 다음 웨이브의 점수/Flux 배율을 두툼하게.",
      overloadMeltdown: "Overload 4단 — 가장 어려운 사전 설정 웨이브. 최상 배율.",
      themeAmber: "코스메틱 팔레트 — 따뜻한 amber CRT. 순수 코스메틱; 효과 없음.",
      themePhosphor: "코스메틱 팔레트 — 빈티지 인광 그린. 순수 코스메틱; 효과 없음.",
    },
  },

  help: {
    eyebrow: '/ /  레 퍼 런 스',
    title: '운용 매뉴얼',
    controls: '조작',
    powerGrid: '전력망',
    powerGridBody:
      '무기는 전력을 소모, Pylon이 공급한다. 수요가 공급을 넘으면 전력망은 정전되어 모든 타워가 둔화 — Pylon을 지어 발사를 유지하라.',
    objective: '목표',
    objectiveBody:
      '15웨이브의 캠페인을 모두 정리하면 승리; 코어의 생명이 0이 되는 순간 패배. Endless 모드는 무한히 진행된다.',
    mazeMode: 'Maze 모드',
    mazeModeBody:
      '개방형 필드의 세 번째 모드: 타워가 타일을 막고 적은 A*로 우회하므로 네가 미로를 만든다. 코어를 완전히 막을 수는 없다.',
    glitches: '글리치',
    towers: '타워',
    abilities: '스킬',
    replayIntro: '↺  인트로 재생',
    closeHint: 'Esc / H / ? 또는 아무 곳이나 클릭해 닫기',
  },

  intro: {
    eyebrow: '/ /  시 스 템   브 리 핑',
    titleBig: 'OVERVOLT',
    subtitle: '코어를 지켜라',
    line1:
      '오염된 프로그램이 회로를 따라 코어로 행군한다. 돌파되기 전에 타워로 파괴하라.',
    line2:
      '⚡ 모든 타워는 전력을 소모. 과도하게 지으면 전력망이 정전 — 모든 타워가 동시에 둔화. Pylon을 지어 전력을 유지하라.',
    line3:
      '조용한 구간에 건설, 그 후 웨이브 시작을 눌러라. 15웨이브를 살아남으면 승리.',
    line4: '마우스로 건설 · 1–5 선택 · 스페이스 시작/정지 · U 업그레이드 · S 판매',
    gotIt: '확인  →',
    gotItHint: '아무 키나 눌러 계속',
  },

  puzzle: {
    label: '퍼즐',
    levels: {
      'p0-first-light': {
        name: '첫 빛',
        hint: 'Pulse를 통로 옆에 설치. 사거리 안의 어떤 타일이든 된다.',
      },
      'p1-long-lance': {
        name: '긴 창',
        hint: '레인에 정렬한 Sniper 하나로 모든 적을 관통.',
      },
      'p2-over-the-wall': {
        name: '벽 너머로',
        hint: '벽은 모든 직사 광선을 차단. 박격포 포탄은 곡선으로 넘어간다.',
      },
      'p3-funhouse-mirror': {
        name: '왜곡 거울',
        hint: '틈새를 꿰는 Sniper로 두 레인을 동시 타격.',
      },
      'p4-surplus-riches': {
        name: '잉여의 풍요',
        hint: '자유 대역 너머로 Pylon을 더 지어라. Arc Coil은 잉여로 빛난다.',
      },
      'p5-pinch-point': {
        name: '압축점',
        hint: 'Cryo로 압축점을 페인트, 주변 Pulse가 무리를 마무리.',
      },
      'p6-cipher-gauntlet': {
        name: 'Cipher 시련',
        hint:
          '박격포로 장갑을 찢고, Cryo로 굽이를 페인트, Sniper로 하단 다리를 관통. 전체 키트를 사용하라.',
      },
      'p7-kernel-echo': {
        name: 'Kernel 메아리',
        hint:
          '박격포는 시야 벽을 넘어 투사. Sniper는 아래로 꿰뚫는다. Power Drain을 주시하라.',
      },
      'p8-overclock-crunch': {
        name: '오버클럭 위기',
        hint:
          'Pulse 하나로는 부족. 폭발 구간에 오버클럭 — 지속 화력으로는 안 된다.',
      },
      'p9-pylon-pinch': {
        name: 'Pylon 압착',
        hint: 'Pylon을 타워 사이에 밀집 — 오라 상한은 좁은 배치를 보상한다.',
      },
    },
    briefing: {
      eyebrow: "퍼즐 브리핑",
      startBtn: "시작 ▷",
      cancelBtn: "취소",
      sectionConstraints: "제약",
      sectionTowers: "타워",
      sectionWinCondition: "승리 조건",
      budgetLabel: "골드 예산",
      wavesLabel: "웨이브",
      towerAllowed: "허용",
      towerForbidden: "잠김",
      resultEyebrowWon: "퍼즐 완료",
      resultEyebrowLost: "퍼즐 실패",
      resultTitleWon: "해결!",
      resultTitleLost: "다시 시도",
      resultStatTowers: "타워",
      resultStatGold: "소비한 골드",
      resultStatLives: "남은 생명",
      resultNextBtn: "다음 레벨 ▷",
      resultReplayBtn: "리플레이",
      resultResetBtn: "시도 리셋",
      resultBackBtn: "선택으로 돌아가기",
    },

    // Sprint 69 — puzzle level-select overlay chrome.
    select: {
      subtitle: "수작업 로직 레벨 — 유일한 승리 배치를 추론하라.",
      close: "닫기",
      cardTitle: 'P{index}  {name}',
      lockedCard: "🔒 잠김",
      solved: "✓ 해결",
      unsolved: "▷ 미해결",
      noSolve: "아직 해결되지 않음",
      bestLine: "베스트: {towers}t · {gold}g · {power}p",
      allowed: "허용: {list}",
      parPlaceholder: "파: — / — / —",
      parRow: "{towers}t · {gold}g · {power}p",
    },
  },

  localeChip:
 {
    label: '언어',
    en: 'EN',
    cs: 'CS',
  },

  resume: {
    title: '이전 런을 재개할까요?',
    resume: '재개',
    newRun: '새 런',
    hint: '[Enter] 재개   ·   [Esc] 새 런',
    detail: "{mode} · {difficulty} · 웨이브 {wave} · 타워 {towers} · {gold}g · {lives}♥",
  },

  // -------- Sprint 69: Kernel boss UI chrome ---------------------------------
  boss: {
    kernelHeader: "⚠  더 커널",
    phase: "페이즈 {phase} · {name}",
    powerDrain: "⚡ 파워 드레인",
    kernelIncoming: "⚠  커널  접근  ⚠",
    phaseName: {
      boot: "BOOT SEQUENCE",
      fork: "FORK BOMB",
      panic: "KERNEL PANIC",
    },
  },

  // -------- Sprint 69: Map registry display strings --------------------------
  maps: {
    default: { label: "Datawash", tagline: "개방형 S자 — 클래식 OVERVOLT." },
    cramped: { label: "Switchyard", tagline: "비좁은 통로 — 모든 타일이 다툼." },
    lines: { label: "Long Lines", tagline: "긴 직선 — 랜스를 안내하라." },
    serpentine: { label: "Snake Run", tagline: "좁은 곡선 — Cryo painter 가 빛난다." },
  },

  // -------- Sprint 69: Ability display names ---------------------------------
  abilities: {
    overcharge: { label: 'Overcharge' },
    airstrike: { label: 'Airstrike' },
    empBurst: { label: 'EMP Burst' },
  },

  // -------- Sprint 69: Overload tier names -----------------------------------
  overload: {
    tierName: {
      nominal: "노미널",
      charged: "충전됨",
      surging: "서지",
      critical: "크리티컬",
      meltdown: "멜트다운",
    },
  },

  // -------- Sprint 69: Endless modifier labels -------------------------------
  endlessMod: {
    armoredSurge: { label: "장갑 돌격" },
    fastPack: { label: "빠른 무리" },
    scatteredSpread: { label: "산개" },
    streakRunners: { label: "Streak 주자" },
    daemonRush: { label: "Daemon 러시" },
  },

  // -------- Sprint 69: Enemy display names (Help roster) ---------------------
  enemies: {
    bit: { label: "Bit" },
    packet: { label: "Packet" },
    daemon: { label: "Daemon" },
    fork: { label: "Fork" },
    cipher: { label: "Cipher" },
    patch: { label: "Patch" },
    buffer: { label: "Buffer" },
    streak: { label: "Streak" },
    kernel: { label: "커널" },
  },

  // -------- Sprint 69: Help-panel enemy one-liners ---------------------------
  enemyRole: {
    bit: "기본 잡몹 — 저렴, 느림, 약함.",
    packet: "빠르고 약함; 코어로 돌진.",
    daemon: "강한 HP 스폰지; 누수 시 강타.",
    fork: "파괴 시 두 Bit 로 분열.",
    cipher: "장갑 — Mortar 부식으로 찢어라.",
    patch: "주변 Glitch 회복; 먼저 처치.",
    buffer: "재생 실드; 버스트로 깨라.",
    streak: "단독 시 스프린트; 슬로우 저항. 경로를 덮어라.",
    kernel: "페이즈 보스 — 에너지 흡수. 다음 페이즈 전에 버스트로 처치.",
  },

  // -------- Sprint 69: Help-panel ability one-liners -------------------------
  abilityRole: {
    overcharge: "그리드 가속: 모든 타워가 짧은 동안 더 빨리 발사. 헤비 웨이브에 맞춰 계획.",
    airstrike: "대상 타일에 일격 폭발. 무리 정리 또는 큰 적 깎기.",
    empBurst: "필드 스턴 — 모든 적이 잠시 정지. 레인이 무너질 때 시간을 사라.",
  },

  // -------- Sprint 69: Tooltip chrome ----------------------------------------
  tooltip: {
    levelOf: "LV {level}/{max}",
    credits: "{cost} 크레딧",
    next: "NEXT  →  LV {level}   {cost} 크레딧",
    cooldown: "쿨다운  {sec}s",
    stat: {
      supply: "공급  +{n}w 그리드에",
      dmgRngCd: "DMG {damage}    RNG {range}    CD {cd}s",
      pwrChain: "PWR {power}w    체인 {chain}",
      pwrAoe: "PWR {power}w    AOE {targets} 표적",
      slow: "SLOW ×{factor}  지속  {sec}s",
      brittle: "취약 +{pct}% 다른 타워 dmg",
      pwrBlast: "PWR {power}w    BLAST {tiles} 타일",
      pwrSingle: "PWR {power}w    1 표적",
    },
  },

  // -------- Sprint 69: Help-panel control-row labels -------------------------
  controls: {
    arm: "Pulse / Arc / Pylon / Cryo / Mortar / Sniper 선택",
    mouse: "타일에 배치 · 타워 클릭으로 선택",
    space: "웨이브 시작 · 웨이브 일시정지",
    fast: "빨리감기 (1× ⇄ 2×)",
    upgradeSell: "선택 타워 업그레이드 / 판매",
    target: "선택 타워의 표적 모드 전환",
    profile: "프로필 열기 (해금 트리 · 스코어)",
    utility: "취소 · 음소거 · 런 재시작",
  },

  // -------- Sprint 69: Unlock-tree node display names ------------------------
  unlocks: {
    towerMortar: { name: "Splash Mortar" },
    towerSniper: { name: "Sniper Rail" },
    abilityAirstrike: { name: "Airstrike" },
    abilityEmp: { name: "EMP Burst" },
    modeEndless: { name: "Endless 모드" },
    modeMaze: { name: "Maze 모드" },
    difficultyHard: { name: "Hard 난이도" },
    overloadCritical: { name: "Overload: 크리티컬" },
    overloadMeltdown: { name: "Overload: 멜트다운" },
    themeAmber: { name: "Amber 터미널" },
    themePhosphor: { name: "인광 그린" },
  },

  // Sprint 73 — 인터랙티브 튜토리얼 (docs/TUTORIAL_DESIGN.md §6).
  tutorial: {
    button: {
      start: '튜토리얼 시작',
      startSub: '처음이세요? 투어를 진행하세요 →',
      replay: '투어 다시 재생',
      replaySub: '기본 다시 보기',
    },
    step: {
      welcome: {
        title: 'OVERVOLT에 오신 것을 환영합니다',
        body: '당신은 고장난 리액터의 방어 AI입니다. 손상된 프로그램이 회로를 따라 Core로 진군합니다 — 돌파되기 전에 제거하세요.',
      },
      path: {
        title: '경로',
        body: '적은 항상 이 S자 경로를 따라 좌측 스폰 타일에서 우측 Core로 이동합니다. 어디에 포탑을 세울지는 당신이 결정합니다.',
      },
      firstPlace: {
        title: '첫 포탑 배치',
        body: 'Pulse Turret은 50 크레딧, 전력 4를 소비합니다. 경로 옆 빛나는 타일을 클릭해 배치하세요.',
      },
      wave1: {
        title: '첫 웨이브 발진',
        body: '웨이브 시작을 누르세요. Pulse는 사정거리 내 적을 자동 사격합니다.',
      },
      towerRoles: {
        title: '다섯 타워, 다섯 역할',
        body: 'Pulse: 어디서나 안정. Sniper: 긴 직선. Mortar: 간접 사격, 경로에서 뒤로. Arc: 전력 잉여로 강화. Cryo: 한 곳을 둔화·취약화.',
      },
      powerGrid: {
        title: '전력망',
        body: '모든 타워는 전력을 소비합니다. 수요가 공급을 초과하면 그리드가 정전되고 모든 타워가 느려집니다. Pylon을 지으세요 — 인접한 Pylon은 발사 속도를 강화합니다.',
      },
      overloadDial: {
        title: 'Overload — 위험 대 보상',
        body: '다음 웨이브를 위해 다이얼을 올리세요: 강한 적, 더 많은 Flux. 내리면 안전.',
      },
      overclock: {
        title: '타워 오버클럭',
        body: '선택된 타워에서 OVERCLOCK을 켜 버스트 창을 얻으세요: 빠른 발사, 더 많은 전력 소모. 버스트 승, 지속 패.',
      },
      abilities: {
        title: '액티브 능력',
        body: '세 슬롯: Overcharge로 전 그리드 버스트. Airstrike로 지속 화염 지대 투하. EMP Burst로 모든 적 기절. 계획적으로 — 패닉 누름 금지.',
      },
      mazePreview: {
        title: 'Maze 모드',
        body: '고정 경로 없음 — 타워가 타일을 막고 적은 A*로 우회합니다. 적이 지나갈 길을 당신이 만듭니다.',
      },
      puzzlePreview: {
        title: 'Puzzle 모드',
        body: '시야선 벽이 있는 수제 논리 레벨. 고정 예산, 고정 웨이브 — 유일한 승리 배치를 찾으세요. 다른 게임.',
      },
      wrapUp: {
        title: '준비 완료',
        body: '런으로 FLUX를 획득하세요 — 프로필에서 사용해 새 타워, 능력, 모드를 해금. Campaign부터 시작; Endless, Maze, Puzzle은 그 뒤에.',
      },
    },
    ui: {
      eyebrow: '단계 {n} / {total}',
      next: '다음 →',
      back: '← 뒤로',
      skip: '투어 건너뛰기',
      play: '플레이  ▸',
      skipConfirm: '투어를 건너뛸까요? 타이틀 화면에서 언제든 다시 재생할 수 있습니다.',
    },
  },
};
